import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Clock4 } from "lucide-react";

export const LOGOUT_REASON_KEY = "obligo_logout_reason";
const WARN_MS = 10 * 60 * 1000;
const hhmm = (iso) => new Date(iso).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });

export const SessionCutoff = () => {
  const { logout } = useAuth();
  const [info, setInfo] = useState(null);
  const [notice, setNotice] = useState(null);
  const load = useCallback(() => api.get("/auth/session-info").then((r) => setInfo(r.data)).catch(() => {}), []);
  useEffect(() => {
    load();
    const t = setInterval(load, 5 * 60 * 1000);
    window.addEventListener("focus", load);
    return () => { clearInterval(t); window.removeEventListener("focus", load); };
  }, [load]);

  useEffect(() => {
    if (!info?.auto_logout || !info.ends_at) return undefined;
    const ends = new Date(info.ends_at).getTime();
    const seenKey = `obligo_afterhours_${info.ends_at}`;
    if (info.after_hours && !sessionStorage.getItem(seenKey)) {
      sessionStorage.setItem(seenKey, "1");
      setNotice("after");
    }
    const timers = [];
    const left = ends - Date.now();
    if (left > WARN_MS) timers.push(setTimeout(() => setNotice("warn"), left - WARN_MS));
    else if (left > 0) setNotice((n) => n || "warn");
    timers.push(setTimeout(async () => {
      sessionStorage.setItem(LOGOUT_REASON_KEY, `Office hours ended at ${hhmm(info.ends_at)}. Your running timer was stopped and saved. You can sign in again; an after-hours session lasts ${info.late_extension_minutes} minutes.`);
      await logout();
    }, Math.max(left - 3000, 0)));
    return () => timers.forEach(clearTimeout);
  }, [info, logout]);

  if (!notice || !info) return null;
  const warn = notice === "warn";
  return (
    <Dialog open onOpenChange={(o) => !o && setNotice(null)}>
      <DialogContent className="sm:max-w-md" data-testid={warn ? "cutoff-warning-dialog" : "after-hours-dialog"}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Clock4 className="w-5 h-5 text-blue-700" />{warn ? "Office hours are ending" : "You're working after office hours"}</DialogTitle>
          <DialogDescription data-testid="cutoff-message">
            {warn
              ? `You'll be signed out automatically at ${hhmm(info.ends_at)}. Any running timer will be stopped and saved, so wrap up and save your work.`
              : `Office hours ended at ${info.office_end}. This session will end at ${hhmm(info.ends_at)} (${info.late_extension_minutes}-minute extension). Any running timer will stop then.`}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter><Button data-testid="cutoff-ok" onClick={() => setNotice(null)} className="bg-blue-700 hover:bg-blue-800 text-white">OK, got it</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
